/**
 * Quor AI Service Client
 * Frontend client for AI-powered features
 */

const API_URL = import.meta.env.VITE_API_URL || '/api'

interface Block {
  id: string
  type: string
  order: number
  [key: string]: unknown
}

interface PricingItem {
  description: string
  quantity: number
  unit_price: number
  total: number
}

interface GenerateProposalResponse {
  success: boolean
  blocks: Block[]
  pricing_items: PricingItem[]
  total_price: number
  title: string
  valid_days: number
  is_fallback?: boolean
}

interface ImproveTextResponse {
  success: boolean
  original: string
  improved: string
  action: string
  error?: string
}

interface GenerateContentResponse {
  success: boolean
  content_type: string
  content?: string
  items?: unknown[]
  error?: string
}

interface RiskAnalysisResponse {
  success: boolean
  risks: string[]
  missing_clauses: string[]
  score: number
  recommendations: string[]
}

interface PriceSuggestionResponse {
  success: boolean
  suggested_price: number | null
  min_price?: number
  max_price?: number
  confidence: number
  source: string
  reasoning?: string
}

interface TranslateResponse {
  success: boolean
  translated_content?: string
  from_language: string
  to_language: string
  style: string
  error?: string
}

class AIService {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(endpoint: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      throw new Error(error.detail || 'AI request failed')
    }

    return response.json()
  }

  /**
   * Generate a complete proposal from a text description
   */
  async generateProposal(
    prompt: string,
    options?: {
      clientName?: string
      clientCompany?: string
      industry?: string
      language?: string
    }
  ): Promise<GenerateProposalResponse> {
    return this.request<GenerateProposalResponse>('/ai/generate-proposal', {
      prompt,
      client_name: options?.clientName,
      client_company: options?.clientCompany,
      industry: options?.industry,
      language: options?.language || 'he'
    })
  }

  /**
   * Improve or modify text content
   * @param text The text to improve
   * @param action One of: improve, shorten, expand, professional, friendly
   */
  async improveText(
    text: string,
    action: 'improve' | 'shorten' | 'expand' | 'professional' | 'friendly' = 'improve'
  ): Promise<ImproveTextResponse> {
    return this.request<ImproveTextResponse>('/ai/improve-text', {
      text,
      action,
      language: 'he'
    })
  }

  /**
   * Generate specific content sections
   * @param contentType One of: intro, benefits, terms, timeline, faq
   * @param context Description of what to generate content for
   */
  async generateContent(
    contentType: 'intro' | 'benefits' | 'terms' | 'timeline' | 'faq',
    context: string
  ): Promise<GenerateContentResponse> {
    return this.request<GenerateContentResponse>('/ai/generate-content', {
      content_type: contentType,
      context,
      language: 'he'
    })
  }

  /**
   * Analyze proposal for risks and missing clauses
   */
  async analyzeRisks(
    proposalContent: string,
    pricingItems: PricingItem[] = []
  ): Promise<RiskAnalysisResponse> {
    return this.request<RiskAnalysisResponse>('/ai/analyze-risks', {
      proposal_content: proposalContent,
      pricing_items: pricingItems,
      language: 'he'
    })
  }

  /**
   * Get AI-powered pricing suggestion
   */
  async suggestPrice(
    serviceDescription: string,
    options?: {
      clientIndustry?: string
      projectSize?: 'small' | 'medium' | 'large'
    }
  ): Promise<PriceSuggestionResponse> {
    return this.request<PriceSuggestionResponse>('/ai/suggest-price', {
      service_description: serviceDescription,
      client_industry: options?.clientIndustry,
      project_size: options?.projectSize || 'medium'
    })
  }

  /**
   * Translate proposal content
   */
  async translate(
    content: string,
    options?: {
      fromLang?: string
      toLang?: string
      style?: 'professional' | 'legal' | 'casual'
    }
  ): Promise<TranslateResponse> {
    return this.request<TranslateResponse>('/ai/translate', {
      content,
      from_lang: options?.fromLang || 'he',
      to_lang: options?.toLang || 'en',
      style: options?.style || 'professional'
    })
  }
}

export const aiService = new AIService(API_URL)
export default aiService
